import React from "react";
import { useSelector } from "react-redux";
import { currencyString } from "@qgiv/core-js";
import { getFormattedPrice } from "@qgiv/core-donor";
import { createEntityReplaceForRegistration } from "@qgiv/p2p-registration";
import { useLocation } from "react-router-dom";
import { useFormikContext } from "formik";
import { selectThemeSettings } from "../../../../../redux/slices/cmsSettingsSlice";
import { selectAllFormSettings } from "../../../../../redux/slices/formSettingsSlice";
import { selectHasParentRegistration } from "../../../../../redux/slices/appSettingsSlice";
import {
    selectAllRegistration,
    selectRegistrationTotals,
} from "../../../../../redux/slices/registrationSlice";
import { selectAllProductSettings } from "../../../../../redux/slices/productSettingsSlice";
import { selectTeams } from "../../../../../redux/slices/teamsSlice";
import { selectAllClassifications } from "../../../../../redux/slices/classificationsSlice";
import {
    selectAllP2pSettings,
    selectBaseUrl,
} from "../../../../../redux/slices/p2pSettingsSlice";
import { ThemeType } from "../../../../../redux/constants";
import TotalSummary from "./TotalSummary";

const ConnectedTotalSummary = () => {
    const { pathname } = useLocation();
    const { values } = useFormikContext();

    const { title, currency } = useSelector(selectAllFormSettings);
    const hasParentRegistration = useSelector(selectHasParentRegistration);
    const registration = useSelector(selectAllRegistration);
    const {
        currentRegistration,
        payment,
        promoCodes,
        promoDiscounts,
        storeCart = [],
        classification,
    } = registration;
    const { transaction = {} } = promoDiscounts;
    const { taxRate = 0 } = useSelector(selectAllProductSettings);
    const teams = useSelector(selectTeams);
    const classifications = useSelector(selectAllClassifications);
    const {
        enableDonationToParticipant,
        enableDonationToTeam,
        enableDonationToEvent,
        enableClassificationForDonation,
        registrationFeeDisplay,
        registrationFeeDisplayText,
    } = useSelector(selectAllP2pSettings);

    const themeSettings = useSelector((state) =>
        selectThemeSettings(ThemeType.SUMMARY, state),
    );
    const {
        p2pGeneral: { subtotalLabel = "Subtotal", totalLabel = "Total" },
        p2pGifts: {
            optionalGiftLabel = "Gift toward your goal",
            optionalGiftLabelForNonFundraisingParticipants = "Gift toward %Entity%'s goal",
            giftAssistLabel = "Extra gift to offset fees",
            giftSubsectionHeading = "Gifts",
        },

        p2pStore: { storeSubsectionHeading = "Add-ons" },
    } = themeSettings;

    const hasPromo = promoCodes.length > 0;

    const {
        giftAssistAmount = 0.0,
        prePromoTransTotal = 0.0,
        registrantSubtotal = 0.0,
        storeSubtotalAmount = 0.0,
        storeTaxAmount = 0.0,
        total = 0.0,
    } = useSelector(selectRegistrationTotals);
    const baseUrl = useSelector(selectBaseUrl);

    const promoTotalDiscounts =
        transaction &&
        transaction.isDiscounted &&
        transaction.promoCodes.length > 0
            ? transaction.promoCodes
            : [];

    const getPaymentValues = () => {
        if (pathname === `${baseUrl}/form/payment`) {
            return values;
        }
        return payment;
    };
    const getRegistrations = () => {
        const { registrations = [] } = registration;
        if (pathname === `${baseUrl}/form/registration`) {
            return registrations.map((reg, index) => {
                if (index === currentRegistration) {
                    return values;
                }
                return reg;
            });
        }
        return registrations;
    };
    const registrations = getRegistrations();
    const { Donation_Amount = "0.00" } = getPaymentValues();

    // Determine which label to give optional gifts based on nfp status
    const isNfp = !!(
        registrations.length > 0 &&
        registrations[0].Is_Nfp &&
        registrations[0].Is_Nfp === true
    );

    const displayNameForHasParentRegistration =
        registrations.length > 0 && registrations[0].First_Name
            ? registrations[0].First_Name
            : "";

    // Needed for the case where the registration fee is $0
    // and a $0 product is added to the cart
    const hasStoreCartItems = storeCart && storeCart.length > 0;

    const entityReplace = createEntityReplaceForRegistration(
        registrations,
        teams,
        classification,
        classifications,
        enableDonationToTeam,
        enableDonationToEvent,
        enableClassificationForDonation,
        title,
    );

    const entityReplacedOptionalGiftLabelForNonFundraisingParticipants =
        optionalGiftLabelForNonFundraisingParticipants.replace(
            "%Entity%",
            entityReplace || "",
        );

    const gift =
        Number(Donation_Amount) > 0
            ? currencyString(Number(Donation_Amount), currency)
            : undefined;

    const processingFee =
        giftAssistAmount && giftAssistAmount > 0
            ? currencyString(giftAssistAmount, currency)
            : undefined;
    const giftSubtotal =
        // eslint-disable-next-line no-nested-ternary
        Number(Donation_Amount) > 0 && giftAssistAmount && giftAssistAmount > 0
            ? currencyString(
                  Number(Donation_Amount) + giftAssistAmount,
                  currency,
              )
            : Number(Donation_Amount) === 0 &&
                giftAssistAmount &&
                giftAssistAmount > 0
              ? currencyString(giftAssistAmount, currency)
              : currencyString(Number(Donation_Amount), currency);
    const storeSubtotal =
        hasStoreCartItems && storeSubtotalAmount > 0
            ? currencyString(storeSubtotalAmount, currency)
            : undefined;
    const formattedStoreTax =
        storeTaxAmount && storeTaxAmount > 0
            ? currencyString(storeTaxAmount, currency)
            : undefined;
    const registrationSubTotal =
        (registrantSubtotal && registrantSubtotal > 0) || hasPromo
            ? getFormattedPrice(
                  registrantSubtotal,
                  currency,
                  registrationFeeDisplay,
                  registrationFeeDisplayText,
                  hasPromo,
              )
            : undefined;

    const formattedTotal =
        (total && total > 0) || hasPromo
            ? getFormattedPrice(
                  total,
                  currency,
                  registrationFeeDisplay,
                  registrationFeeDisplayText,
                  hasPromo,
              )
            : undefined;

    const formattedPrePromoTotal = getFormattedPrice(
        prePromoTransTotal,
        currency,
        registrationFeeDisplay,
        registrationFeeDisplayText,
        hasPromo,
    );

    const totalSummaryProps = {
        currency,
        gift,
        giftSubtotal,
        hasStoreCartItems,
        formattedPrePromoTotal,
        formattedStoreTax,
        processingFee,
        promoTotalDiscounts,
        storeSubtotal,
        registrationSubTotal,
        taxRate,
        formattedTotal,
        isCms: false,
        isNfp,
        // Theme state
        giftAssistLabel,
        giftSubsectionHeading,
        // eslint-disable-next-line no-nested-ternary
        optionalGiftLabel: enableDonationToParticipant
            ? hasParentRegistration
                ? // This is not customizable in CMS, it is only for fundraising center registrations
                  // where the optional donation will actually go to the sub's account
                  `Gift towards ${displayNameForHasParentRegistration}'s fundraising goal.`
                : optionalGiftLabel
            : entityReplacedOptionalGiftLabelForNonFundraisingParticipants,
        // donations made for a NFP additional registration through the
        // fundraising center will be attributed to the primary registration
        optionalGiftLabelForNonFundraisingParticipants:
            hasParentRegistration && enableDonationToParticipant
                ? optionalGiftLabel
                : entityReplacedOptionalGiftLabelForNonFundraisingParticipants,
        totalLabel,
        subtotalLabel,
        storeSubsectionHeading,
    };

    return <TotalSummary {...totalSummaryProps} />;
};

export default ConnectedTotalSummary;
